import { createFileRoute } from '@tanstack/react-router';
import { Plus, Users } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProtectedRoute } from '@/components/auth/protected-route';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { CreateUserDialog } from '@/components/users/create-user-dialog';
import { UserList } from '@/components/users/user-list';
import { useToast } from '@/hooks/use-toast';
import { useCreateUser, useDeleteUser, useUsers } from '@/hooks/use-users';
import type { CreateUserRequest } from '@/services/api';

export const Route = createFileRoute('/users')({
  component: () => (
    <ProtectedRoute>
      <UsersPage />
    </ProtectedRoute>
  ),
});

function UsersPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const { data: usersData, isLoading } = useUsers();
  const createUser = useCreateUser();
  const deleteUser = useDeleteUser();

  const users = usersData?.users ?? {};

  const handleCreateUser = (data: CreateUserRequest) => {
    createUser.mutate(data, {
      onSuccess: () => {
        setIsCreateDialogOpen(false);
        toast({
          title: t('users.userCreated'),
          description: t('users.userCreatedDescription'),
        });
      },
      onError: (error) => {
        const message = error instanceof Error ? error.message : t('users.createError');
        toast({
          title: t('common.error'),
          description: message,
          variant: 'destructive',
        });
      },
    });
  };

  const handleDeleteUser = (userId: string) => {
    deleteUser.mutate(userId, {
      onSuccess: () => {
        toast({
          title: t('users.userDeleted'),
          description: t('users.userDeletedDescription'),
        });
      },
      onError: (error) => {
        const message = error instanceof Error ? error.message : t('users.deleteError');
        toast({
          title: t('common.error'),
          description: message,
          variant: 'destructive',
        });
      },
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">{t('users.title')}</h1>
        <div className="animate-pulse space-y-3">
          <div className="h-16 bg-gray-200 rounded-lg" />
          <div className="h-16 bg-gray-200 rounded-lg" />
          <div className="h-16 bg-gray-200 rounded-lg" />
        </div>
      </div>
    );
  }

  const hasUsers = Object.keys(users).length > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-8 w-8" />
          <h1 className="text-3xl font-bold">{t('users.title')}</h1>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          {t('users.createUser')}
        </Button>
      </div>

      {hasUsers ? (
        <UserList users={users} onDeleteUser={handleDeleteUser} isDeleting={deleteUser.isPending} />
      ) : (
        <EmptyState
          icon={Users}
          title={t('users.noUsers')}
          description={t('users.noUsersDescription')}
          action={
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              {t('users.createUser')}
            </Button>
          }
        />
      )}

      <CreateUserDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSubmit={handleCreateUser}
        isLoading={createUser.isPending}
      />
    </div>
  );
}
